import type { ChangeEvent } from "react";
import { useCallback, useMemo, useReducer, useRef, useEffect } from "react";
import type { Basic } from "unsplash-js/src/methods/photos/types";

import { t } from "@/i18n-js/instance";

import { Uploader } from "../../Uploader";
import type { Area, Point } from "../Cropper";
import {
  checkIsImageFile,
  createRandomName,
  getCroppedImage,
  isImageURL,
  readURL,
} from "../functions";
import { initialState, reducer } from "./useImageEditorState";

interface UseImageEditorOptions {
  attachImage: ((url: string, signedId: string) => void) | null;
  closeModal: () => void;
  formClass: string;
  initialFile?: File | null;
  inputFieldName: string;
  isCropEnabled: boolean;
  isPublic: boolean;
  saveImage: ((signedId: string) => void) | null;
  setHasFile: (hasFile: boolean) => void;
  setIsLoading: (loading: boolean) => void;
  showModal: boolean;
  uploadProgress: (progress: number, fileName: string) => void;
}

const createUploader = (
  file: File,
  afterUpload: (itemUrl: string, signedId: string) => void,
  formClass: string,
  inputFieldName: string,
  uploadProgress: (progress: number, fileName: string) => void,
  uploadMetadata: Record<string, string> | undefined,
) =>
  new Uploader(
    file,
    afterUpload,
    formClass,
    inputFieldName,
    uploadProgress,
    uploadMetadata,
  );

// Workaround to create a FileList since it has no constructor
const createFileList = (file: File): FileList => {
  const dataTransfer = new DataTransfer();
  dataTransfer.items.add(file);
  return dataTransfer.files;
};

export const useImageEditor = ({
  attachImage,
  closeModal,
  formClass,
  inputFieldName,
  isCropEnabled,
  isPublic,
  saveImage,
  setHasFile,
  setIsLoading,
  showModal,
  uploadProgress,
  initialFile = null,
}: UseImageEditorOptions) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const prevShowModalRef = useRef(showModal);
  const consumedFileRef = useRef<File | null>(null);

  useEffect(() => {
    if (prevShowModalRef.current && !showModal) {
      dispatch({ type: "RESET" });
      setHasFile(false);
    }
    prevShowModalRef.current = showModal;
  }, [showModal, setHasFile]);

  const uploadMetadata = useMemo(
    () => (isPublic ? { public: "true" } : undefined),
    [isPublic],
  );

  const afterUpload = useCallback(
    (itemUrl: string, signedId: string) => {
      if (attachImage) {
        attachImage(itemUrl, signedId);
      }
      if (saveImage) {
        saveImage(signedId);
      }
      setIsLoading(false);
    },
    [attachImage, saveImage, setIsLoading],
  );

  const startUploadIfNoCrop = useCallback(
    (file: File) => {
      if (!isCropEnabled) {
        closeModal();
        const uploader = createUploader(
          file,
          afterUpload,
          formClass,
          inputFieldName,
          uploadProgress,
          uploadMetadata,
        );
        uploader.start();
      }
    },
    [
      afterUpload,
      closeModal,
      formClass,
      inputFieldName,
      isCropEnabled,
      uploadMetadata,
      uploadProgress,
    ],
  );

  const addUnsplash = async (item: Basic) => {
    dispatch({ type: "SET_PREVIEW", preview: item.urls.regular });
    setHasFile(true);
    try {
      setIsLoading(true);
      const response = await fetch(item.urls.regular);
      const blob = await response.blob();
      const file = new File([blob], `unsplash-${item.id}.jpg`, {
        type: "image/jpeg",
      });
      startUploadIfNoCrop(file);
    } catch (error) {
      console.error("Error handling Unsplash image:", error);
      dispatch({
        type: "SET_ERROR",
        message: t("image_upload.unsplash_error"),
      });
    }
  };

  const insertFiles = useCallback(
    async (files: FileList | null) => {
      if (!files) return;
      setIsLoading(true);

      const filesArray = Array.from(files);

      for (const file of filesArray) {
        if (checkIsImageFile(file.type)) {
          const parsedFile = await readURL(file);
          if (!parsedFile) continue;
          dispatch({
            type: "SELECT_FILE",
            fileList: files,
            preview: parsedFile,
          });
          setHasFile(true);
          startUploadIfNoCrop(file);
        } else {
          setIsLoading(false);
          dispatch({
            type: "SET_ERROR",
            message: t("image_upload.invalid_file_type_error"),
          });
        }
      }
    },
    [setHasFile, setIsLoading, startUploadIfNoCrop],
  );

  useEffect(() => {
    if (!showModal || !initialFile) {
      if (!initialFile) consumedFileRef.current = null;
      return;
    }
    if (consumedFileRef.current === initialFile) return;
    consumedFileRef.current = initialFile;

    void insertFiles(createFileList(initialFile));
  }, [showModal, initialFile, insertFiles]);

  const onChangeEmbedUrl = (event: ChangeEvent<HTMLInputElement>) => {
    dispatch({ type: "SET_EMBED_URL", url: event.target.value });
  };

  const addEmbed = async () => {
    try {
      await isImageURL(state.embedUrl);
      setIsLoading(true);
      dispatch({ type: "SET_PREVIEW", preview: state.embedUrl });
      setHasFile(true);

      const response = await fetch(state.embedUrl);
      const blob = await response.blob();
      const file = new File([blob], `embed-${createRandomName()}.jpg`, {
        type: response.headers.get("content-type") || "image/jpeg",
      });
      startUploadIfNoCrop(file);
    } catch (error: unknown) {
      if (error instanceof Error) {
        dispatch({ type: "SET_ERROR", message: error.message });
      }
    }
  };

  const onCropComplete = useCallback((_: Area, croppedAreaPixels: Area) => {
    dispatch({ type: "SET_CROP_AREA", area: croppedAreaPixels });
  }, []);

  const removeImage = useCallback(() => {
    dispatch({ type: "RESET" });
    setHasFile(false);
  }, [setHasFile]);

  const saveCroppedImage = useCallback(async () => {
    try {
      setIsLoading(true);
      if (!state.stringFile || !state.cropArea) {
        throw new Error(t("image_upload.file_cropped_area_error"));
      }

      const fileArray = state.fileList ? Array.from(state.fileList) : [];
      const firstFile = fileArray[0] ?? null;

      const fileName = firstFile ? firstFile.name : createRandomName();
      const fileType = firstFile ? firstFile.type : "image/jpeg";

      const croppedImage = await getCroppedImage(
        state.stringFile,
        state.cropArea,
        fileName,
        fileType,
      );
      closeModal();
      removeImage();

      const uploader = createUploader(
        croppedImage,
        afterUpload,
        formClass,
        inputFieldName,
        uploadProgress,
        uploadMetadata,
      );
      uploader.start();
    } catch (error: unknown) {
      setIsLoading(false);
      console.error(error);
      if (error instanceof Error) {
        dispatch({ type: "SET_ERROR", message: error.message });
      }
    }
  }, [
    state.stringFile,
    state.cropArea,
    state.fileList,
    afterUpload,
    closeModal,
    formClass,
    inputFieldName,
    removeImage,
    uploadProgress,
    uploadMetadata,
    setIsLoading,
  ]);
  const clearError = useCallback(() => {
    dispatch({ type: "CLEAR_ERROR" });
  }, []);

  const setCrop = (crop: Point) => dispatch({ type: "SET_CROP", crop });
  const setZoom = (zoom: number) => dispatch({ type: "SET_ZOOM", zoom });

  return {
    state,
    addUnsplash,
    clearError,
    insertFiles,
    onChangeEmbedUrl,
    addEmbed,
    onCropComplete,
    saveCroppedImage,
    removeImage,
    setCrop,
    setZoom,
  };
};
